import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormGroup,
  Spinner,
  OffcanvasBody,
  Offcanvas,
  OffcanvasHeader,
} from "reactstrap";
import WaiterService from "../../../services/OrderingServices/WaiterService";
import toast from "react-hot-toast";
import Select from "react-select";
import OrganizationService from "../../../services/OrderingServices/OrganizationService";
import SimpleBar from "simplebar-react";

const WaiterForm = ({ isOpen, toggle, selectedItem, fetchData }) => {
  const [passwordShow, setPasswordShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roleData, setRoleData] = useState([]);

  const [detail, setDetail] = useState();
  const fetchDetail = async () => {
    try {
      const response = await OrganizationService.orgDetail(selectedItem?.id);
      setDetail(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchDetail();
  }, [selectedItem?.id]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: detail?.firstName || "",
      lastName: detail?.lastName || "",
      email: detail?.email || "",
      phoneNumber: detail?.phoneNumber ? String(detail.phoneNumber) : "",
      address: detail?.address || "",
      password: detail?.password || "",
      roles: detail?.roles || [],
      shouldShowTable: detail?.shouldShowTable || false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      phoneNumber: Yup.string()
        .required("Phone number is required")
        .matches(/^[0-9]+$/, "Phone number must be digits only")
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number must be exactly 10 digits"),
      address: Yup.string().required("Address is required"),
      email: Yup.string()
        .email("Invalid email address")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        )
        .required("Email is required"),
      password: selectedItem?.id
        ? Yup.string().notRequired()
        : Yup.string()
            .min(6, "Password must be at least 6 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
              "Password must contain at least one uppercase, one lowercase, and one number"
            )
            .required("Password is required"),
      roles: selectedItem?.id
        ? Yup.array().notRequired()
        : Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required("Role ID is required"),
                name: Yup.string().required("Role name is required"),
              })
            )
            .min(1, "At least one role is required")
            .required("Roles are required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: String(values.phoneNumber),
          address: values.address,
          shouldShowTable: values.shouldShowTable,
        };

        if (!selectedItem?.id) {
          payload.email = values.email;
          payload.password = values.password;
          payload.roles = values.roles
            ? values.roles.map((role) => ({
                id: role.id,
                name: role.name,
                normalizedName: role.normalizedName || "",
                concurrencyStamp: role.concurrencyStamp || "",
              }))
            : [];
        } else {
          payload.roles = values.roles
            ? values.roles.map((role) => ({
                id: role.id,
                name: role.name,
                normalizedName: role.normalizedName || "",
                concurrencyStamp: role.concurrencyStamp || "",
              }))
            : [];
        }

        if (selectedItem?.id) {
          await WaiterService.update(selectedItem?.id, payload);
          toast.success("Staff updated successfully");
        } else {
          await WaiterService.create(payload);
          toast.success("staff created successfully");
          resetForm();
        }

        fetchData();
        toggle();
      } catch (error) {
        toast.error(
          error.response?.data?.title ||
            "Failed to submit form. Please try again."
        );
        console.error("Error submitting form:", error.response?.data || error);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchRole = async () => {
    try {
      const response = await WaiterService.authRole();
      setRoleData(response);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchRole();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm({
        values: {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          address: "",
          password: "",
          roles: [],
          shouldShowTable: false,
        },
        touched: {},
        errors: {},
      });
      setPasswordShow(false);
    } else {
      formik.resetForm({
        values: {
          firstName: selectedItem?.firstName || "",
          lastName: selectedItem?.lastName || "",
          email: selectedItem?.email || "",
          phoneNumber: selectedItem?.phoneNumber
            ? String(selectedItem.phoneNumber)
            : "",
          address: selectedItem?.address || "",
          password: selectedItem?.password || "",
          roles: selectedItem?.roles || [],
        },
        touched: {},
        errors: {},
      });
    }
  }, [isOpen, selectedItem]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      fontSize: "1rem",
      borderColor: state.isFocused ? "#86b7fe" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.25)" : null,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "6px 12px",
    }),
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      backdrop={true}
      style={{ width: "420px" }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom">
        <i className="ri-stack-line me-2"></i> Staff Form
      </OffcanvasHeader>

      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column"
        style={{ height: "calc(100vh - 73px)" }}
      >
        {/* SCROLLABLE CONTENT */}
        <div className="flex-grow-1 overflow-auto p-3">
          {/* First Name */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">First Name</Label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              className="form-control shadow-sm"
              placeholder="Enter first name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.firstName}
              invalid={!!(formik.touched.firstName && formik.errors.firstName)}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <FormFeedback type="invalid">
                {formik.errors.firstName}
              </FormFeedback>
            )}
          </div>

          {/* Last Name */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Last Name</Label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              className="form-control shadow-sm"
              placeholder="Enter last name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.lastName}
              invalid={!!(formik.touched.lastName && formik.errors.lastName)}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <FormFeedback type="invalid">
                {formik.errors.lastName}
              </FormFeedback>
            )}
          </div>

          {/* Phone Number */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Phone Number</Label>
            <Input
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control shadow-sm"
              placeholder="Enter phone number"
              value={formik.values.phoneNumber}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/\D/g, "");
                formik.setFieldValue("phoneNumber", onlyNums);
              }}
              onBlur={formik.handleBlur}
              invalid={
                !!(formik.touched.phoneNumber && formik.errors.phoneNumber)
              }
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <FormFeedback type="invalid">
                {formik.errors.phoneNumber}
              </FormFeedback>
            )}
          </div>

          {/* Address */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">Address</Label>
            <Input
              id="address"
              name="address"
              type="text"
              className="form-control shadow-sm"
              placeholder="Enter address"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address}
              invalid={!!(formik.touched.address && formik.errors.address)}
            />
            {formik.touched.address && formik.errors.address && (
              <FormFeedback type="invalid">
                {formik.errors.address}
              </FormFeedback>
            )}
          </div>

          {/* Email & Password (if new staff) */}
          {!selectedItem?.id && (
            <>
              <div className="mb-3">
                <Label className="fw-bold mb-2">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  className="form-control shadow-sm"
                  placeholder="Enter email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  invalid={!!(formik.touched.email && formik.errors.email)}
                />
                {formik.touched.email && formik.errors.email && (
                  <FormFeedback type="invalid">
                    {formik.errors.email}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-3 position-relative">
                <Label className="fw-bold mb-2">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type={passwordShow ? "text" : "password"}
                  className="form-control shadow-sm"
                  placeholder="Enter password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  invalid={
                    !!(formik.touched.password && formik.errors.password)
                  }
                />
                <button
                  type="button"
                  onClick={() => setPasswordShow(!passwordShow)}
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted mt-2"
                >
                  {passwordShow ? (
                    <i className="bx bxs-show"></i>
                  ) : (
                    <i className="bx bxs-hide"></i>
                  )}
                </button>
                {formik.touched.password && formik.errors.password && (
                  <FormFeedback type="invalid">
                    {formik.errors.password}
                  </FormFeedback>
                )}
              </div>

              <div className="mb-3">
                <Label className="fw-bold mb-2">Roles</Label>
                <Select
                  id="roles"
                  name="roles"
                  options={roleData}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={formik.values.roles}
                  onChange={(selectedOptions) =>
                    formik.setFieldValue("roles", selectedOptions || [])
                  }
                  onBlur={() => formik.setFieldTouched("roles", true)}
                  isMulti
                  isClearable
                  placeholder="Select roles"
                  classNamePrefix="react-select"
                  className={
                    formik.touched.roles && formik.errors.roles
                      ? "is-invalid"
                      : ""
                  }
                />
                {formik.touched.roles && formik.errors.roles && (
                  <div
                    className="text-danger mt-1"
                    style={{ fontSize: "0.875rem" }}
                  >
                    {formik.errors.roles}
                  </div>
                )}
              </div>
            </>
          )}
          <FormGroup switch>
            <Input
              type="switch"
              role="switch"
              name="shouldShowTable"
              checked={formik.values.shouldShowTable}
              onChange={(e) =>
                formik.setFieldValue("shouldShowTable", e.target.checked)
              }
            />
            <Label check>Show Table</Label>
          </FormGroup>
        </div>

        {/* FIXED FOOTER BUTTONS */}
        <div className="bg-white border-top p-3 d-flex justify-content-between gap-2">
          <Button
            color="danger"
            onClick={toggle}
            className="w-50 shadow-sm border"
          >
            Cancel
          </Button>
          <Button
            color="success"
            type="submit"
            disabled={loading}
            className="w-50 shadow-sm"
          >
            {loading && <Spinner size="sm" className="me-2" />}
            {loading
              ? selectedItem?.id
                ? "Updating..."
                : "Creating..."
              : selectedItem?.id
              ? "Update"
              : "Create"}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default WaiterForm;
